import { convertFromBytes, convertToBytes } from '@/utils/fileSizeFormatter';

export const SIZE_CONDITION_UNITS = ['GB', 'TB'];

const DEFAULT_UNIT = 'GB';

const BYTES_PER_TB = convertToBytes(1, 'TB');

/**
 * Only bytes are stored, so `1000 GB` and `1 TB` are the same condition and the
 * unit a size was entered in cannot be recovered from it. Preferring TB for the
 * sizes that divide evenly into it reproduces what the user most likely typed;
 * anything else reads more naturally in GB.
 */
export const toSizeDraft = (storedBytes: string | undefined) => {
  const bytes = Number(storedBytes);
  if (!storedBytes || !Number.isFinite(bytes) || bytes < 0) {
    return { amount: '', unit: DEFAULT_UNIT };
  }

  const unit = bytes > 0 && bytes % BYTES_PER_TB === 0 ? 'TB' : DEFAULT_UNIT;
  return { amount: String(convertFromBytes(bytes, unit)), unit };
};

/**
 * `undefined` for anything that is not a size, which is what the editor stores
 * as an empty condition value so the wizard's own completeness check rejects it.
 *
 * A byte count above `MAX_SAFE_INTEGER` counts as not a size either: it is not
 * exactly representable, and `String` renders it in exponential notation, which
 * the API's int64 parse rejects.
 */
export const toStoredSizeBytes = (amount: string, unit: string) => {
  const parsed = Number(amount);
  if (amount === '' || !Number.isFinite(parsed) || parsed < 0) {
    return undefined;
  }

  const bytes = Math.round(convertToBytes(parsed, unit));
  return bytes > Number.MAX_SAFE_INTEGER ? undefined : bytes;
};
